/**
 * Catalogue Page Component
 * Displays the company PDF catalogue in an embedded inline viewer
 * with a prominent download option. Falls back to a download card
 * on devices/browsers that cannot render inline PDFs (e.g., some mobiles).
 */

import React, { useEffect } from 'react';
import { FaDownload, FaFilePdf, FaBookOpen } from 'react-icons/fa';
import './Catalogue.css';
import cataloguePdf from '../assets/Catalogue/SJ Polyzen Industries Catalogue.pdf';

const Catalogue = () => {
    /**
     * Intersection Observer for fade-in animations
     */
    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                }
            });
        }, observerOptions);

        const fadeElements = document.querySelectorAll('.fade-in');
        fadeElements.forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return (
        <div className="catalogue-page">
            {/* Page Header */}
            <section className="catalogue-header">
                <div className="container">
                    <h1 className="page-title">Product Catalogue</h1>
                    <p className="page-subtitle">
                        Browse our complete range of recycled PPCP injection-grade granules
                    </p>
                </div>
            </section>

            {/* Catalogue Actions + Viewer */}
            <section className="section catalogue-section">
                <div className="container">
                    {/* Action Bar */}
                    <div className="catalogue-actions fade-in">
                        <div className="catalogue-actions-info">
                            <FaBookOpen className="catalogue-actions-icon" />
                            <div>
                                <h3>SJ Polyzen Industries Catalogue</h3>
                                <p>View the catalogue below or download a copy for offline reference.</p>
                            </div>
                        </div>
                        <a
                            href={cataloguePdf}
                            download="SJ-Polyzen-Industries-Catalogue.pdf"
                            className="btn btn-primary catalogue-download-btn"
                        >
                            <FaDownload aria-hidden="true" />
                            <span>Download Catalogue</span>
                        </a>
                    </div>

                    {/* Inline PDF Viewer (desktop / capable browsers) */}
                    <div className="catalogue-viewer fade-in">
                        <object
                            data={cataloguePdf}
                            type="application/pdf"
                            className="catalogue-pdf-object"
                            aria-label="SJ Polyzen Industries Product Catalogue PDF"
                        >
                            {/* Fallback for browsers that cannot display PDFs inline */}
                            <div className="catalogue-fallback">
                                <FaFilePdf className="catalogue-fallback-icon" />
                                <h3>Preview not supported on this device</h3>
                                <p>Your browser cannot display the PDF inline. You can download it instead.</p>
                                <a
                                    href={cataloguePdf}
                                    download="SJ-Polyzen-Industries-Catalogue.pdf"
                                    className="btn btn-primary"
                                >
                                    <FaDownload aria-hidden="true" />
                                    <span>Download Catalogue</span>
                                </a>
                            </div>
                        </object>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Catalogue;
